/**
 * ShopQRCodeSection — v1.8.8
 *
 * QR Code section for the "Dati Negozio" page.
 *
 * Renders:
 *  - preview of the shop's QR code (256px, ECC=H)
 *  - four actions: download A4 poster, download 1080² social card,
 *    copy image to clipboard, copy public URL to clipboard
 *
 * All artwork (poster, social) is composited client-side on `<canvas>`.
 * No external QR service is used — the `qrcode` npm package generates the
 * matrix locally.
 *
 * The generated posters use a color scheme matching Kyloo's landing
 * (deep blue #1D4ED8 background block + white cards, orange dot from the
 * logo mark #FF6A2B for the accent).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { Button } from '../ui/button';
import { Download, Copy, Link as LinkIcon, Loader2, Share2, ImageIcon } from 'lucide-react';

// Colors match design_guidelines / landing page.
const KYLOO_BLUE = '#1D4ED8';
const KYLOO_BLUE_DARK = '#1E3A8A';
const KYLOO_ORANGE = '#FF6A2B';
const KYLOO_TEXT = '#0F172A';
const KYLOO_MUTED = '#64748B';

// Where the shop's public storefront lives. We use the production origin
// (kyloo.it) instead of the preview URL so printed posters always point to
// the right place even when generated in preview.
const PUBLIC_BASE = 'https://kyloo.it';

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Draw a rounded rectangle path. */
function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Wrap text into lines that fit `maxWidth`. Returns an array of lines. */
function wrapText(ctx, text, maxWidth) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let current = '';
  for (const w of words) {
    const candidate = current ? `${current} ${w}` : w;
    if (ctx.measureText(candidate).width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}

async function generateQrDataUrl(url, size = 1024) {
  return QRCode.toDataURL(url, {
    errorCorrectionLevel: 'H',
    margin: 1,
    width: size,
    color: { dark: KYLOO_TEXT, light: '#FFFFFF' },
  });
}

/**
 * Renders the A4 printable poster.
 * Canvas: 2480x3508 (A4 @ 300 dpi). Portrait.
 */
async function renderPosterA4(shop, publicUrl, logoImg) {
  const W = 2480;
  const H = 3508;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Background: soft off-white so print doesn't waste ink but stays clean.
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, W, H);

  // Top blue header block with logo.
  const headerH = 520;
  ctx.fillStyle = KYLOO_BLUE;
  ctx.fillRect(0, 0, W, headerH);
  // subtle darker gradient at the bottom edge of the header for depth
  const grad = ctx.createLinearGradient(0, headerH - 120, 0, headerH);
  grad.addColorStop(0, 'rgba(0,0,0,0)');
  grad.addColorStop(1, 'rgba(0,0,0,0.15)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, headerH - 120, W, 120);

  // Logo centered in header (max 300px height, keep aspect ratio).
  if (logoImg) {
    const logoTargetH = 320;
    const scale = logoTargetH / logoImg.height;
    const logoTargetW = logoImg.width * scale;
    ctx.drawImage(
      logoImg,
      (W - logoTargetW) / 2,
      (headerH - logoTargetH) / 2,
      logoTargetW,
      logoTargetH,
    );
  } else {
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 200px "Outfit","Segoe UI",sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('kyloo', W / 2, headerH / 2);
  }

  // White QR card, centered horizontally, below header.
  const cardW = 1920;
  const cardH = 2100;
  const cardX = (W - cardW) / 2;
  const cardY = headerH + 220;
  ctx.fillStyle = '#FFFFFF';
  ctx.strokeStyle = '#E2E8F0';
  ctx.lineWidth = 6;
  roundedRect(ctx, cardX, cardY, cardW, cardH, 48);
  ctx.fill();
  ctx.stroke();

  // Shop name centered near top of card.
  ctx.fillStyle = KYLOO_TEXT;
  ctx.font = 'bold 96px "Outfit","Segoe UI",sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const shopName = (shop?.nome || 'Il tuo negozio').toString();
  const nameLines = wrapText(ctx, shopName, cardW - 160);
  let y = cardY + 100;
  for (const line of nameLines.slice(0, 2)) {
    ctx.fillText(line, W / 2, y);
    y += 110;
  }

  // Small orange accent under name.
  ctx.fillStyle = KYLOO_ORANGE;
  ctx.beginPath();
  ctx.arc(W / 2, y + 40, 18, 0, Math.PI * 2);
  ctx.fill();
  y += 100;

  // QR code image.
  const qrPng = await generateQrDataUrl(publicUrl, 1400);
  const qrImg = await loadImage(qrPng);
  const qrSize = 1200;
  ctx.drawImage(qrImg, (W - qrSize) / 2, y, qrSize, qrSize);
  y += qrSize + 60;

  // CTA text.
  ctx.fillStyle = KYLOO_TEXT;
  ctx.font = '600 68px "Outfit","Segoe UI",sans-serif';
  const ctaLines = wrapText(
    ctx,
    'Scansiona il QR Code e scopri tutti i nostri prodotti',
    cardW - 200,
  );
  for (const line of ctaLines) {
    ctx.fillText(line, W / 2, y);
    y += 90;
  }

  // URL under CTA in muted color.
  ctx.fillStyle = KYLOO_MUTED;
  ctx.font = '500 44px "Menlo","Consolas",monospace';
  ctx.fillText(publicUrl, W / 2, y + 20);

  // Footer strip.
  const footerH = 180;
  ctx.fillStyle = KYLOO_BLUE_DARK;
  ctx.fillRect(0, H - footerH, W, footerH);
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '500 52px "Outfit","Segoe UI",sans-serif';
  ctx.textBaseline = 'middle';
  ctx.fillText('kyloo.it — Il tuo negozio, in tasca a tutti', W / 2, H - footerH / 2);

  return canvas;
}

/**
 * Renders the 1080x1080 social card.
 */
async function renderSocialCard(shop, publicUrl, logoImg) {
  const S = 1080;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');

  // Deep blue background with a subtle vertical gradient.
  const bg = ctx.createLinearGradient(0, 0, 0, S);
  bg.addColorStop(0, KYLOO_BLUE);
  bg.addColorStop(1, KYLOO_BLUE_DARK);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, S, S);

  // Logo top-center on the blue.
  if (logoImg) {
    const targetH = 120;
    const scale = targetH / logoImg.height;
    const targetW = logoImg.width * scale;
    ctx.drawImage(logoImg, (S - targetW) / 2, 60, targetW, targetH);
  } else {
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 84px "Outfit","Segoe UI",sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('kyloo', S / 2, 60);
  }

  // White rounded card containing QR + name.
  const cardW = 820;
  const cardH = 720;
  const cardX = (S - cardW) / 2;
  const cardY = 230;
  ctx.fillStyle = '#FFFFFF';
  roundedRect(ctx, cardX, cardY, cardW, cardH, 40);
  ctx.fill();

  // Shop name inside card.
  ctx.fillStyle = KYLOO_TEXT;
  ctx.font = 'bold 44px "Outfit","Segoe UI",sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const shopName = (shop?.nome || 'Il tuo negozio').toString();
  const nameLines = wrapText(ctx, shopName, cardW - 80);
  let y = cardY + 40;
  for (const line of nameLines.slice(0, 1)) {
    ctx.fillText(line, S / 2, y);
    y += 60;
  }

  // QR code image inside card.
  const qrPng = await generateQrDataUrl(publicUrl, 700);
  const qrImg = await loadImage(qrPng);
  const qrSize = 560;
  ctx.drawImage(qrImg, (S - qrSize) / 2, y + 10, qrSize, qrSize);

  // Call to action below the card, on the blue background.
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '600 40px "Outfit","Segoe UI",sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Scopri tutti i nostri prodotti', S / 2, cardY + cardH + 60);

  // Handle: shop URL (short form).
  ctx.fillStyle = KYLOO_ORANGE;
  ctx.font = '500 30px "Outfit","Segoe UI",sans-serif';
  const displayUrl = publicUrl.replace(/^https?:\/\//, '');
  ctx.fillText(displayUrl, S / 2, cardY + cardH + 110);

  return canvas;
}

function canvasToBlob(canvas, type = 'image/png', quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function slugify(str) {
  return String(str || 'negozio')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'negozio';
}

export default function ShopQRCodeSection({ shop }) {
  const [qrPreview, setQrPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const logoRef = useRef(null);

  const publicUrl = shop?.id ? `${PUBLIC_BASE}/negozio/${shop.id}` : PUBLIC_BASE;

  // Generate a preview once (and any time the URL changes).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const dataUrl = await generateQrDataUrl(publicUrl, 512);
        if (alive) setQrPreview(dataUrl);
      } catch (e) {
        console.debug('QR preview generation failed:', e?.message);
      }
    })();
    return () => { alive = false; };
  }, [publicUrl]);

  // Preload the Kyloo logo used in the composite exports.
  useEffect(() => {
    let alive = true;
    loadImage('/logo-512.png')
      .then((img) => { if (alive) logoRef.current = img; })
      .catch((err) => console.debug('Logo preload failed:', err?.message));
    return () => { alive = false; };
  }, []);

  const handleDownloadPoster = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const canvas = await renderPosterA4(shop, publicUrl, logoRef.current);
      const blob = await canvasToBlob(canvas);
      downloadBlob(blob, `kyloo-qr-poster-${slugify(shop?.nome)}.png`);
      toast.success('Poster A4 scaricato');
    } catch (e) {
      console.error('Poster generation error', e);
      toast.error('Impossibile generare il poster');
    } finally {
      setBusy(false);
    }
  }, [shop, publicUrl, busy]);

  const handleDownloadSocial = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const canvas = await renderSocialCard(shop, publicUrl, logoRef.current);
      const blob = await canvasToBlob(canvas);
      downloadBlob(blob, `kyloo-qr-social-${slugify(shop?.nome)}.png`);
      toast.success('Immagine social scaricata');
    } catch (e) {
      console.error('Social generation error', e);
      toast.error('Impossibile generare l\'immagine social');
    } finally {
      setBusy(false);
    }
  }, [shop, publicUrl, busy]);

  const handleCopyImage = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const canvas = await renderSocialCard(shop, publicUrl, logoRef.current);
      const blob = await canvasToBlob(canvas);
      // Try the Clipboard API — silently fallback to download if unsupported
      // or blocked (Safari, HTTP contexts, permission denied).
      if (navigator.clipboard && window.ClipboardItem) {
        try {
          await navigator.clipboard.write([
            new window.ClipboardItem({ 'image/png': blob }),
          ]);
          toast.success('Immagine copiata negli appunti');
          return;
        } catch (err) {
          console.debug('Clipboard image write failed:', err?.message);
        }
      }
      // Fallback: download so the user still gets the file.
      downloadBlob(blob, `kyloo-qr-social-${slugify(shop?.nome)}.png`);
      toast.info('Il tuo browser non supporta la copia immagine — file scaricato.');
    } catch (e) {
      console.error('Copy image error', e);
      toast.error('Impossibile copiare l\'immagine');
    } finally {
      setBusy(false);
    }
  }, [shop, publicUrl, busy]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Link copiato.');
    } catch (e) {
      console.debug('Copy link failed:', e?.message);
      toast.error('Impossibile copiare il link');
    }
  }, [publicUrl]);

  return (
    <section
      className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 mt-6"
      data-testid="shop-qr-section"
    >
      <header className="mb-4">
        <h2 className="text-lg sm:text-xl font-bold text-slate-800 flex items-center gap-2">
          <Share2 className="w-5 h-5 text-blue-600" />
          QR Code del negozio
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Il QR Code punta alla tua vetrina pubblica su Kyloo. Scaricalo, stampalo o condividilo sui social.
        </p>
      </header>

      <div className="flex flex-col md:flex-row md:items-center gap-6">
        {/* Preview */}
        <div className="flex-shrink-0 mx-auto md:mx-0">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm w-[240px] h-[240px] flex items-center justify-center">
            {qrPreview ? (
              <img
                src={qrPreview}
                alt="QR Code negozio"
                className="w-full h-full object-contain"
                data-testid="shop-qr-preview"
              />
            ) : (
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            )}
          </div>
          <p
            className="text-xs text-slate-500 text-center mt-2 font-mono break-all max-w-[240px]"
            data-testid="shop-qr-url"
          >
            {publicUrl}
          </p>
        </div>

        {/* Actions */}
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            onClick={handleDownloadPoster}
            disabled={busy}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl h-11 justify-start"
            data-testid="qr-download-poster"
          >
            {busy
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-2" />}
            Scarica poster A4
          </Button>
          <Button
            onClick={handleDownloadSocial}
            disabled={busy}
            variant="outline"
            className="border-blue-600 text-blue-700 hover:bg-blue-50 rounded-xl h-11 justify-start"
            data-testid="qr-download-social"
          >
            {busy
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <ImageIcon className="w-4 h-4 mr-2" />}
            Scarica per social (1080²)
          </Button>
          <Button
            onClick={handleCopyImage}
            disabled={busy}
            variant="outline"
            className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-xl h-11 justify-start"
            data-testid="qr-copy-image"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copia immagine
          </Button>
          <Button
            onClick={handleCopyLink}
            variant="outline"
            className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-xl h-11 justify-start"
            data-testid="qr-copy-link"
          >
            <LinkIcon className="w-4 h-4 mr-2" />
            Copia link
          </Button>
        </div>
      </div>
    </section>
  );
}
